<template>
  <mf-card-container v-model="search" :title="$t('campanhas.resultados_por_produto')" useSearch placeholder="Buscar produto">
    <template v-slot:filters>
      <export-data :data-fetcher="getDataToExport" filename="resultados_por_produto" ghost />
      <headers-data-table-settings
        :key="headersSettingsName"
        :headers.sync="headers"
        :default-headers="getDefaultHeadersOptions()"
        :headers-settings-name="headersSettingsName"
      />
    </template>
    <v-data-table
      v-model="selectedInternalItems"
      :items="items"
      :loading="loading"
      :headers="computedHeaders"
      :search="search"
      hide-default-footer
      disable-pagination
      :no-data-text="noDataText"
      class="mt-4"
    >
      <template v-slot:header.discounts="{ header }">
        <mf-tooltip top max-width="192px" :disabled="isCashbackOrPointsCurrentOfferType">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ header.text }}
              <mf-icon v-if="!isCashbackOrPointsCurrentOfferType" small color="grey">info</mf-icon>
            </span>
          </template>
          <span>
            Valor calculado com base no cupom fiscal, utilizando o preço 1 e 2 aplicados no PDV, considerando todos os descontos aplicados (ofertas, vouchers e
            cashback).</span
          >
        </mf-tooltip>
      </template>

      <template v-slot:header.offers_schedule_discounts="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ header.text }}
              <mf-icon small color="grey">info</mf-icon>
            </span>
          </template>
          <span>Valor calculado a partir da configuração da agenda de ofertas, utilizando o preço 1 e preço 2 configurados na plataforma.</span>
        </mf-tooltip>
      </template>

      <template v-slot:header.impact="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ header.text }}
              <mf-icon small color="grey">info</mf-icon>
            </span>
          </template>
          <span>{{ impactMessage }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:header.rebate="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>{{ rebateMessage }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:header.sold_units="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Total de vendas dos SKU dentro da oferta no período.</span>
        </mf-tooltip>
      </template>

      <template v-slot:header.eligible_products="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Total de vendas dos SKU que cumpriram os requisitos para aplicação de desconto.</span>
        </mf-tooltip>
      </template>

      <template v-if="isMercapromoCampaign" v-slot:header.revenue="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Faturamento gerado nesta oferta</span>
        </mf-tooltip>
      </template>

      <template v-if="isMercapromoCampaign" v-slot:header.tickets="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Cupons da oferta que possuem esse produto</span>
        </mf-tooltip>
      </template>

      <template v-if="isMercapromoCampaign" v-slot:header.clients="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>CPF únicos que receberam desconto</span>
        </mf-tooltip>
      </template>

      <template v-if="isMercapromoCampaign" v-slot:header.earn_value="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Total de benefício (cashback ou voucher) gerado por esse produto</span>
        </mf-tooltip>
      </template>

      <template v-if="isMercapromoCampaign" v-slot:header.average_discount="{ header }">
        <mf-tooltip top max-width="192px">
          <template v-slot:activator="{ on }">
            {{ header.text }}
            <mf-icon small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>Média do valor de venda da unidade, considerando os descontos da oferta.</span>
        </mf-tooltip>
      </template>

      <template v-slot:item._id="{ item }">
        <span v-if="isMercapromoCampaign">{{ item._id }}</span>
        <a v-else class="link" target="_blank" :href="`#/produtos/detalhes-produto/${item._id}`">{{ item._id }}</a>
      </template>
      <template v-slot:item.name="{ item }">
        {{ item.name }}
      </template>
      <template v-slot:item.activated_clients="{ item }">
        {{ item.activated_clients
        }}<mf-quick-actions
          v-if="showDiscounts && !hideQuickActionFromFranchisee && !isMercapromoCampaign && !isCashbackOrPointsCurrentOfferType"
          v-bind="offersScheduleActivatedClientsQuickActions(item)"
        />
      </template>
      <template v-slot:item.clients="{ item }">
        <div v-if="hideQuickActionFromFranchisee">
          {{ item.clients | abreviation({ isInteger: true }) }}
        </div>
        <div v-else>
          {{ item.clients | abreviation({ isInteger: true })
          }}<mf-quick-actions v-if="item.quickActionsProps && !isMercapromoCampaign && !isCashbackOrPointsCurrentOfferType" v-bind="item.quickActionsProps" />
        </div>
      </template>
      <template v-slot:item.tickets="{ item }">
        {{ item.tickets | abreviation({ isInteger: true }) }}
      </template>
      <template v-slot:item.quantity="{ item }">
        {{ locale(item.quantity, 2) | localeString(0, 2) }}
      </template>
      <template v-slot:item.revenue="{ item }">
        <span>{{ locale(item.revenue, 2) | localeString(2) | currency }}</span>
      </template>
      <template v-if="showDiscounts" v-slot:item.discounts="{ item }">
        <div v-if="!isPointsCurrentOfferType">
          {{ locale(item.discounts, 2) | localeString(2) | currency }}
        </div>

        <div v-else>
          {{ item.discounts | localeString(0) | abreviation({ isInteger: true }) }}
        </div>
      </template>
      <template v-if="showDiscounts" v-slot:item.offers_schedule_discounts="{ item }">
        {{ locale(item.offers_schedule_discounts, 2) | localeString(2) | currency }}
      </template>
      <template v-slot:item.impact="{ item }">
        {{ locale(item.impact, 2) | localeString(2) | percent }}
      </template>
      <template v-slot:item.rebate="{ item }">
        {{ locale(item.rebate.replace('.', ','), 2) | localeString(2) | currency }}
      </template>
      <template v-slot:item.average_discount="{ item }">
        {{ locale(item.average_discount.replace('.', ','), 2) | localeString(2) | currency }}
      </template>
      <template v-slot:item.sold_units="{ item }">
        {{ locale(item.sold_units, 2) | localeString(0, 2) }}
      </template>
      <template v-slot:item.eligible_products="{ item }">
        <span>{{ locale(item.eligible_products, 2) | localeString(0, 2) }}</span>
      </template>
      <template v-slot:item.earn_value="{ item }">
        <span>{{ locale(item.earn_value, 2) | localeString(0, 2) | currency }}</span>
      </template>
      <template v-if="getSafe(() => items.length) && !search.length" v-slot:body.append="{ headers, items }">
        <tr style="background-color: #f7f7f7">
          <td v-for="(header, i) in headers" :key="i">
            <div v-if="header.value === '_id'">
              <mf-tooltip top max-width="192px">
                <template v-slot:activator="{ on }">
                  <div class="d-flex align-center">
                    <span>Total:</span>
                    <mf-icon class="ml-1" small color="cinza-0" v-on="on"> info </mf-icon>
                  </div>
                </template>
                <span>
                  O total é calculado considerando clientes e cupons únicos. Um mesmo cliente pode comprar diferentes produtos, e um produto pode aparecer em
                  diversos cupons. Por isso a soma dos valores da tabela pode não ser igual ao total.
                </span>
              </mf-tooltip>
            </div>

            <div v-if="header.value === 'name'"></div>

            <div v-if="header.value === 'activated_clients'">
              {{ itemsTotal.activated_clients }}
              <mf-quick-actions
                v-if="showDiscounts && !hideQuickActionFromFranchisee && !isMercapromoCampaign && !isCashbackOrPointsCurrentOfferType"
                v-bind="offersScheduleTotalActivatedClientsQuickActions()"
              />
            </div>

            <div v-if="header.value === 'clients'">
              {{ itemsTotal.clients | abreviation({ isInteger: true })
              }}<mf-quick-actions
                v-if="showDiscounts && !hideQuickActionFromFranchisee && !isMercapromoCampaign && !isCashbackOrPointsCurrentOfferType"
                v-bind="offersScheduleTotalBoughtClientsQuickActions()"
              />
            </div>

            <div v-if="header.value === 'tickets'">
              {{ itemsTotal.tickets | abreviation({ isInteger: true }) }}
            </div>

            <div v-if="header.value === 'quantity'">
              {{ locale(itemsTotal.quantity, 2) | localeString(0, 2) }}
            </div>

            <div v-if="header.value === 'revenue'">
              <span>{{ locale(itemsTotal.revenue, 2) | localeString(2) | currency }}</span>
            </div>

            <div v-if="header.value === 'discounts' && !isPointsCurrentOfferType">
              {{ locale(itemsTotal.discounts, 2) | localeString(2) | currency }}
            </div>

            <div v-else-if="header.value === 'discounts' && isPointsCurrentOfferType">
              {{ itemsTotal.discounts | abreviation({ isInteger: true }) }}
            </div>

            <div v-if="header.value === 'offers_schedule_discounts'">
              {{ locale(itemsTotal.offers_schedule_discounts, 2) | localeString(2) | currency }}
            </div>

            <div v-if="header.value === 'impact'">
              {{ locale(itemsTotal.impact, 2) | localeString(2) | percent }}
            </div>

            <div v-if="header.value === 'rebate'">
              {{ locale(itemsTotal.rebate, 2) | localeString(2) | currency }}
            </div>

            <div v-if="header.value === 'eligible_products'">
              <span>{{ locale(itemsTotal.eligible_products, 2) | localeString(0, 2) }}</span>
            </div>

            <div v-if="header.value === 'sold_units'">
              {{ locale(itemsTotal.sold_units, 2) | localeString(0, 2) }}
            </div>

            <div v-if="header.value === 'earn_value'">
              <span>{{ locale(itemsTotal.earn_value, 2) | localeString(0, 2) | currency }}</span>
            </div>

            <div v-if="header.value === 'average_discount'">
              {{ locale(itemsTotal.average_discount, 2) | localeString(2) | currency }}
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </mf-card-container>
</template>

<script>
import { sortKeepTotal, localeString } from '@/config/helpers.js'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
import {
  criteriaGroup,
  campaignReceivedAndPurchasedSpecificProductCriteria,
  campaignOpenedAndPurchasedSpecificProductCriteria,
  clientsBoughtProductsOffersSchedule,
  activatedProductsOffersScheduleCriteria,
  activatedSpecificProductOffersScheduleCriteria,
  isInUnitsCriteria,
  boughtSpecificProductsHighlightOffersCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'
import cloneDeep from 'lodash/cloneDeep'
import ExportData from '@/components/export-data/ExportData.vue'

export default {
  components: {
    ExportData,
    HeadersDataTableSettings,
  },
  props: {
    loading: Boolean,
    metrics: Object,
    hideQuickActionFromFranchisee: Boolean,
    showDiscounts: Boolean,
    baseMetric: {
      type: String,
      default: 'received',
    },
    campaignModel: {
      type: String,
      default: 'sales',
    },
    campaign: Object,
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    offerSchedule: {
      type: Object,
      default: () => {},
    },
    currentOfferTypeKeyName: {
      type: String,
      default: '',
    },
    isIndustryCampaign: {
      type: Boolean,
      default: false,
    },
    isMercapromoCampaign: {
      type: Boolean,
      default: false,
    },
    isHighlight: {
      type: Boolean,
      default: false,
    },
    rebateMetrics: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    search: '',
    sortTotal: sortKeepTotal,
    locale: localeString,
    allHeadersOptions: [],
    selectedInternalItems: [],
    headers: [],
    offerSalesKeyNames: {
      offer_sales: 'offer_sales',
      percentage_offer_sales: 'percentage_offer_sales',
      dynamic_pricing_percentage_offer_sales: 'dynamic_pricing_percentage_offer_sales',
      takepay_offer_sales: 'takepay_offer_sales',
      combo_offer_sales: 'combo_offer_sales',
      target_products: 'target_products',
      cashback_percentage_offer_sales: 'cashback_percentage_offer_sales',
      cashback_absolute_offer_sales: 'cashback_absolute_offer_sales',
      points_offer_sales: 'points_offer_sales',
    },
    rebateMessage:
      'A estimativa do reembolso é feita através da multiplicação de unidades vendidas com desconto e reembolso unitário. O valor pode demorar até 72h para atualizar.',
  }),
  computed: {
    headersSettingsName() {
      let headerName
      const offerscheduleStr = this.showDiscounts ? '-show-discounts' : ''
      const activatableStr = this.campaignModel === 'activatable' ? '-activatable' : ''
      headerName = 'products_results' + offerscheduleStr + activatableStr

      if (this.isCashbackOrPointsCurrentOfferType) {
        if (this.isCashbackCurrentOfferType) return headerName + '-cashback-type'
        else return headerName + '-points-type'
      }
      return headerName
    },
    noDataText() {
      return `Não há produtos neste ${this.isHighlight ? 'destaque' : 'campanha'}`
    },
    impactMessage() {
      return `Impacto dest${this.isHighlight ? 'e destaque' : 'a campanha'} na quantidade deste produto que foi vendida no período.`
    },
    computedHeaders() {
      const sizeWithoutCodeAndProduct = 100 - (24 + 8.5)
      const headers = this.headers.filter((header) => header.isActive)
      const activableHeadersSize = headers.filter((header) => !header.isRequired).length
      return headers.map((header) => {
        return {
          ...header,
          width: header.width ? header.width : `${sizeWithoutCodeAndProduct / activableHeadersSize}%`,
        }
      })
    },
    isCashbackOrPointsCurrentOfferType() {
      return this.isCashbackCurrentOfferType || this.isPointsCurrentOfferType
    },
    isCashbackCurrentOfferType() {
      return [this.offerSalesKeyNames.cashback_percentage_offer_sales, this.offerSalesKeyNames.cashback_absolute_offer_sales].includes(
        this.currentOfferTypeKeyName
      )
    },
    isBenefitOfferType() {
      return this.currentOfferTypeKeyName === 'nextbuycashback' || this.currentOfferTypeKeyName === 'dynamic_rule'
    },
    isMercapromoAbsoluteDiscount() {
      return this.isMercapromoCampaign && this.currentOfferTypeKeyName === 'general'
    },
    isMercapromoPercentageDiscount() {
      return this.isMercapromoCampaign && this.currentOfferTypeKeyName === 'percentage'
    },
    isMercapromoDynamicPricingPercentageDiscount() {
      return this.isMercapromoCampaign && this.currentOfferTypeKeyName === 'dynamic_pricing_percentage'
    },
    isPointsCurrentOfferType() {
      return this.offerSalesKeyNames.points_offer_sales === this.currentOfferTypeKeyName
    },
    defaultHeaders() {
      const headers = [
        {
          text: 'Código',
          value: '_id',
          width: '8.5%',
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '24%',
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Clientes que compraram',
          value: 'clients',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: `Cupons ${this.isHighlight ? 'fiscais' : ''}`,
          value: 'tickets',
          isActive: !this.showDiscounts,
          isRequired: false,
          isDefault: false,
        },
        {
          text: `Unidades vendidas ${this.isHighlight ? '' : 'com desconto'}`,
          value: 'quantity',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: `Faturamento ${this.isHighlight ? '' : 'total'}`,
          value: 'revenue',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Impacto',
          value: 'impact',
          isActive: !this.showDiscounts,
          isRequired: false,
          isDefault: false,
        },
      ]

      if (this.showDiscounts) {
        headers.splice(6, 0, {
          text: this.offerColumnLabel,
          value: 'discounts',
          isActive: true,
          isRequired: false,
          isDefault: true,
        })

        if (!this.isCashbackOrPointsCurrentOfferType) {
          headers.splice(7, 0, {
            text: 'Desconto da gestão',
            value: 'offers_schedule_discounts',
            isActive: true,
            isRequired: false,
            isDefault: true,
          })
        }
      }

      if (this.campaignModel === 'activatable') {
        headers.splice(2, 0, {
          text: 'Clientes que ativaram',
          value: 'activated_clients',
          isActive: true,
          isRequired: false,
          isDefault: true,
        })
      }

      if (this.isIndustryCampaign) {
        headers.push({
          text: 'Reembolso',
          value: 'rebate',
          isActive: true,
          isRequired: false,
          isDefault: false,
        })
      }

      return headers
    },
    mercapromoHeaders() {
      let mercapromoHeaders = [
        {
          text: 'Código',
          value: '_id',
          width: '8.5%',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Produto',
          value: 'name',
          width: '24%',
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Unidades vendidas',
          value: 'sold_units',
          isActive: true,
          isRequired: false,
          isDefault: false,
        },
        {
          text: 'Unidades elegíveis',
          value: 'eligible_products',
          isActive: true,
          isRequired: false,
          isDefault: false,
        },
        {
          text: this.isBenefitOfferType ? 'Benefício gerado' : this.isMercapromoPercentageDiscount ? 'Unidades com desconto' : 'Unidades gratuitas',
          value: this.isBenefitOfferType ? 'earn_value' : 'quantity',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Clientes beneficiados',
          value: 'clients',
          isActive: false,
          isRequired: false,
          isDefault: false,
        },
        {
          text: 'Cupons fiscais',
          value: 'tickets',
          isActive: false,
          isRequired: false,
          isDefault: false,
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
      ]

      if (this.isMercapromoAbsoluteDiscount) {
        mercapromoHeaders.push({
          text: 'Reembolso',
          value: 'rebate',
          isActive: true,
          isRequired: false,
          isDefault: false,
        })
      }

      if (this.isMercapromoPercentageDiscount) {
        mercapromoHeaders.push({
          text: 'Média do desconto',
          value: 'average_discount',
          isActive: true,
          isRequired: false,
          isDefault: true,
        })
      }

      if (this.isMercapromoAbsoluteDiscount || this.isMercapromoPercentageDiscount) {
        mercapromoHeaders.push({
          text: 'Impacto',
          value: 'impact',
          isActive: true,
          isRequired: false,
          isDefault: false,
        })
      }

      return mercapromoHeaders
    },
    offerColumnLabel() {
      if (this.isCashbackOrPointsCurrentOfferType) {
        if (this.isCashbackCurrentOfferType) return 'Cashback distribuido'
        else return 'Pontos distribuídos'
      }
      return 'Desconto concedido'
    },
    segmentationCriteriaByBaseMetric() {
      return {
        received: campaignReceivedAndPurchasedSpecificProductCriteria,
        opened: campaignOpenedAndPurchasedSpecificProductCriteria,
      }
    },
    items() {
      let discountsLabel = ''

      if (this.isCashbackOrPointsCurrentOfferType) {
        discountsLabel = this.isCashbackCurrentOfferType ? 'cashback' : 'points'
      } else {
        discountsLabel = 'discounts'
      }

      if (!this.metrics?.products_metrics?.table) return []

      return this.metrics.products_metrics.table.map((row) => ({
        _id: row.product.sku,
        name: row.product.name,
        quantity: row[this.baseMetric].quantity,
        activated_clients: row[this.baseMetric].activated_clients,
        discounts: row[this.baseMetric][discountsLabel],
        offers_schedule_discounts: row[this.baseMetric].offers_schedule_discounts,
        revenue: row[this.baseMetric].revenue,
        tickets: row[this.baseMetric].tickets,
        clients: this.isBenefitOfferType ? row.earn?.clients : row[this.baseMetric].clients,
        impact: this.isHighlight ? row[this.baseMetric].impactedPercentage : this.getSafe(() => row[this.baseMetric].quantity / row.total.quantity) * 100 || 0,
        quickActionsProps: this.getQuickActionsPerRow(row),
        rebate: this.localeString(this.getProductRebate(row.product.sku), 2),
        eligible_products: row.eligible?.quantity || 0,
        sold_units: row.total?.quantity || 0,
        earn_value: row.earn?.total_value || 0,
        average_discount: row[this.baseMetric].average_discount?.toFixed(2) || 0,
      }))
    },
    itemsTotal() {
      let discountsLabel = ''

      if (this.isCashbackOrPointsCurrentOfferType) {
        discountsLabel = this.isCashbackCurrentOfferType ? 'cashback' : 'points'
      } else {
        discountsLabel = 'discounts'
      }

      if (this.isHighlight) {
        return {
          _id: '',
          name: '',
          quantity: this.metrics.products_metrics.productsTotalMetrics.quantity,
          activated_clients: this.metrics.products_metrics.productsTotalMetrics.quantity,
          revenue: this.metrics.products_metrics.productsTotalMetrics.revenue,
          tickets: this.metrics.products_metrics.productsTotalMetrics.tickets,
          clients: this.metrics.products_metrics.productsTotalMetrics.clients,
          impact: this.metrics.products_metrics.productsTotalMetrics.impactedPercentage,
        }
      } else {
        return {
          _id: '',
          name: '',
          quantity: this.metrics?.products_metrics?.totals[this.baseMetric]?.quantity || 0,
          activated_clients: this.metrics?.products_metrics?.totals[this.baseMetric]?.activated_clients || 0,
          discounts: this.metrics?.products_metrics?.totals[this.baseMetric]?.[discountsLabel] || 0,
          offers_schedule_discounts: this.metrics?.products_metrics?.totals[this.baseMetric]?.offers_schedule_discounts || 0,
          revenue: this.metrics?.products_metrics?.totals[this.baseMetric]?.revenue || 0,
          tickets: this.metrics?.products_metrics?.totals[this.baseMetric]?.tickets || 0,
          clients: this.isBenefitOfferType ? this.getClientsSum : this.metrics?.products_metrics?.totals[this.baseMetric]?.clients || 0,
          impact:
            this.getSafe(() => this.metrics?.products_metrics?.totals[this.baseMetric]?.quantity / this.metrics?.products_metrics?.totals?.total?.quantity) *
              100 || 0,
          rebate: this.localeString(this.rebateMetrics?.rebate, 2),
          eligible_products: this.metrics?.eligible_metrics?.quantity || 0,
          sold_units: this.metrics?.products_metrics?.totals?.total?.quantity || 0,
          earn_value: this.getEarnValueSum,
          average_discount: this.metrics?.products_metrics?.totals[this.baseMetric]?.average_discount || 0,
        }
      }
    },
    getEarnValueSum() {
      if (!this.metrics?.products_metrics?.table) return 0
      let acc = 0
      this.metrics?.products_metrics?.table.map((row) => {
        acc += row.earn?.total_value || 0
      })
      return acc
    },
    getClientsSum() {
      if (!this.metrics?.products_metrics?.table) return 0
      let acc = 0
      this.metrics?.products_metrics?.table.map((row) => {
        acc += row.earn?.clients || 0
      })
      return acc
    },
  },
  mounted() {
    if (this.headers.length < 1) this.headers = this.isMercapromoCampaign ? cloneDeep(this.mercapromoHeaders) : cloneDeep(this.defaultHeaders)
  },
  methods: {
    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.items.map((item) => this.computedHeaders.map((header) => item[header.value]))
      return { headers, rows }
    },
    getQuickActionsPerRow(row) {
      if (this.campaign) {
        return this.campaignsQuickActions(row)
      } else {
        return this.offersScheduleBoughtClientsQuickActions(row)
      }
    },
    campaignsQuickActions(row) {
      const segmentationCriteria = this.segmentationCriteriaByBaseMetric[this.baseMetric]

      const startDate = this.campaign.initial_date || this.campaign.start_date
      const finalDate = this.campaign.final_date || this.campaign.end_date
      const strStartDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const strFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
      const quickInitialDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('yyyyMMdd')
      const quickFinalDate = DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('yyyyMMdd')

      if (this.isHighlight) {
        return {
          key: `highlight-products-${row.product.sku}`,
          quickActionGroupId: `highlight-products-${row.product.sku}`,
          quickActionGroup: 'highlight-products',
          shortDescription: `Clientes que compraram o produto ${row.product.name} no destaque ${this.campaign.title}`,
          longDescription: `Clientes que compraram o produto ${row.product.name} no destaque ${this.campaign.title} entre ${strStartDate} e ${strFinalDate}`,
          quickActionGroupValue: row.product.sku,
          segmentationGroup: (products, logicOperator) =>
            criteriaGroup(
              boughtSpecificProductsHighlightOffersCriteria(
                this.campaign._id,
                products,
                quickInitialDate,
                quickFinalDate,
                this.campaign.unitsConfiguration.map((unit) => unit.id_erp),
                logicOperator
              )
            ),
        }
      }

      return {
        key: `campaign-products-${row.product.sku}`,
        quickActionGroupId: `campaign-products-${row.product.sku}`,
        quickActionGroup: 'campaign-products',
        shortDescription: `Clientes que ${this.baseMetric === 'received' ? 'receberam' : 'receberam e abriram'} a campanha ${
          this.campaign.title
        } e compraram produtos divulgados no período de mensuração`,
        longDescription: `Clientes que ${this.baseMetric === 'received' ? 'receberam' : 'receberam e abriram'} a campanha ${
          this.campaign.title
        } e compraram produtos divulgados no período de mensuração entre ${strStartDate} e ${strFinalDate}`,
        quickActionGroupValue: row.product.sku,
        segmentationGroup: (products, logicOperator) =>
          criteriaGroup([
            segmentationCriteria([this.campaign._id], products, logicOperator),
            ...(this.unitsConfiguration ? isInUnitsCriteria(this.unitsConfiguration, quickInitialDate, quickFinalDate) : []),
          ]),
      }
    },
    offersScheduleBoughtClientsQuickActions(row) {
      const productId = row.product.sku
      const offerId = this.$route.params.id
      const units = this.offerSchedule.units.map((u) => {
        return {
          dataType: ['PHYSICAL'],
          value: u.id_erp,
        }
      })
      return {
        key: `offer-schedule-${row.product.sku}`,
        quickActionGroupId: `offer-schedule-products-${row.product.sku}`,
        shortDescription: `Clientes que compraram ${row.product.name} na campanha`,
        longDescription: `Clientes que compraram ${row.product.name} na campanha ${this.offerSchedule.name} nas ${units.length} lojas`,
        quickActionGroupValue: row.product.sku,
        segmentationGroup: () => criteriaGroup(clientsBoughtProductsOffersSchedule([offerId], units, [productId])),
      }
    },
    offersScheduleTotalBoughtClientsQuickActions() {
      const offerId = this.$route.params.id
      const units = this.offerSchedule.units.map((u) => {
        return {
          dataType: ['PHYSICAL'],
          value: u.id_erp,
        }
      })
      const totalProductsIds = this.items.map((i) => i._id)

      return {
        key: 'offer-schedule-products-total',
        quickActionGroupId: 'offer-schedule-products-total',
        shortDescription: `Clientes que compraram produtos em oferta`,
        longDescription: `Clientes que compraram produtos em oferta na campanha ${this.offerSchedule.name} nas ${units.length} lojas`,
        segmentationGroup: () => criteriaGroup(clientsBoughtProductsOffersSchedule([offerId], units, totalProductsIds)),
      }
    },
    offersScheduleActivatedClientsQuickActions(product) {
      if (!this.showDiscounts) return {}
      const productId = product._id
      const offerId = this.$route.params.id

      return {
        key: 'offer-schedule-activated-product',
        quickActionGroupId: 'offer-schedule-activated-products',
        shortDescription: `Clientes que ativaram o produto ${product.name} da oferta`,
        longDescription: `Clientes que ativaram o produto ${product.name} da campanha ${this.offerSchedule.name}`,
        segmentationGroup: () => criteriaGroup(activatedSpecificProductOffersScheduleCriteria([offerId], [productId])),
      }
    },
    offersScheduleTotalActivatedClientsQuickActions() {
      const offerId = this.$route.params.id

      return {
        key: 'offer-schedule-activated-products-total',
        quickActionGroupId: 'offer-schedule-activated-products',
        shortDescription: `Clientes que ativaram produtos da oferta`,
        longDescription: `Clientes que ativaram produtos da campanha ${this.offerSchedule.name}`,
        segmentationGroup: () => criteriaGroup(activatedProductsOffersScheduleCriteria([offerId])),
      }
    },
    clearSelectedInternalItems() {
      this.selectedInternalItems = []
    },
    getDefaultHeadersOptions() {
      return this.isMercapromoCampaign ? cloneDeep(this.mercapromoHeaders) : cloneDeep(this.defaultHeaders)
    },
    getProductRebate(sku) {
      return this.rebateMetrics?.product_metrics?.find((productMetric) => productMetric.id === sku)?.rebate ?? 0
    },
  },
}
</script>

<style lang="scss" scoped>
.blur-value {
  filter: blur(3px);
}
</style>
